import React, { useMemo } from "react";
import * as echarts from "echarts";
import ScreeningPolicyCard from "./ScreeningPolicyCard";

/* ---------- tiny echarts card ---------- */

function useEchart(option) {
  const ref = React.useRef(null);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // init once (no theme applied - keep default gray background)
    if (!chartRef.current) {
      chartRef.current = echarts.init(el, undefined, { renderer: "canvas" });
    }

    // apply option
    if (option)
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });

    // ensure first paint matches container size
    requestAnimationFrame(() => chartRef.current?.resize());

    // resize on window + container size changes
    const handle = () => chartRef.current?.resize();
    window.addEventListener("resize", handle);
    const ro = new ResizeObserver(handle);
    ro.observe(el);

    return () => {
      window.removeEventListener("resize", handle);
      ro.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []); // init

  React.useEffect(() => {
    if (chartRef.current && option) {
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });
      requestAnimationFrame(() => chartRef.current?.resize());
    }
  }, [option]);

  return ref;
}

function ChartCard({ option }) {
  const ref = useEchart(option);
  return (
    <div className="chart-card">
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

/* ---------- dataset IDs (from you) ---------- */
const AROPE_ID = "68c95f779cc6b1f763767f2e";
const AGE_ID = "68c95f778103780660e56926";
const IMM_ID = "68c95f77723d33c80e51cc31";
const SPE_ID = "68c95f77de22a0e9a3bb3db5";

/* ---------- helpers ---------- */
function applyTextColor(option) {
  if (!option) return option;
  const isDark = document.body.getAttribute('data-theme') === 'dark';
  const textColor = isDark ? '#e0e0e0' : '#333';

  return {
    ...option,
    title: option.title ? { ...option.title, textStyle: { ...option.title.textStyle, color: textColor } } : undefined,
    legend: option.legend ? { ...option.legend, textStyle: { ...option.legend.textStyle, color: textColor } } : undefined,
    xAxis: option.xAxis ? (Array.isArray(option.xAxis)
      ? option.xAxis.map(axis => ({ ...axis, axisLabel: { ...axis.axisLabel, color: textColor }, nameTextStyle: { ...axis.nameTextStyle, color: textColor } }))
      : { ...option.xAxis, axisLabel: { ...option.xAxis.axisLabel, color: textColor }, nameTextStyle: { ...option.xAxis.nameTextStyle, color: textColor } })
      : undefined,
    yAxis: option.yAxis ? (Array.isArray(option.yAxis)
      ? option.yAxis.map(axis => ({ ...axis, axisLabel: { ...axis.axisLabel, color: textColor }, nameTextStyle: { ...axis.nameTextStyle, color: textColor } }))
      : { ...option.yAxis, axisLabel: { ...option.yAxis.axisLabel, color: textColor }, nameTextStyle: { ...option.yAxis.nameTextStyle, color: textColor } })
      : undefined,
  };
}

const num = (v) => {
  const n = Number(String(v).replaceAll(",", "").trim());
  return Number.isFinite(n) ? n : null;
};
const getEls = (dataById, id) => dataById?.[id]?.elements || [];
const yearCols = (row) =>
  Object.keys(row || {})
    .filter((k) => /^\d{4}$/.test(String(k)))
    .sort();

/* ---------- AROPE charts ---------- */

function aropeByGender(elements) {
  const rows = elements.filter((r) => r["Demo Group Type"] === "Gender");
  if (!rows.length) return null;
  const years = yearCols(rows[0]);

  const men = rows.find(
    (r) => String(r["Demographic Group"]).toLowerCase() === "men"
  );
  const women = rows.find(
    (r) => String(r["Demographic Group"]).toLowerCase() === "women"
  );

  return {
    title: { text: "AROPE by Gender", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series: [
      {
        name: "Men",
        type: "line",
        smooth: true,
        data: years.map((y) => num(men?.[y])),
      },
      {
        name: "Women",
        type: "line",
        smooth: true,
        data: years.map((y) => num(women?.[y])),
      },
    ],
  };
}

function aropeByAge(elements) {
  const rows = elements.filter((r) => r["Demo Group Type"] === "Age");
  if (!rows.length) return null;
  const years = yearCols(rows[0]);
  const series = rows.map((r) => ({
    name: r["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(r[y])),
  }));
  return {
    title: { text: "AROPE by Age Category", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { type: "scroll", top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

function aropeByNUTS1_2024(elements) {
  const rows = elements.filter((r) => r["Demo Group Type"] === "NUTS-1");
  if (!rows.length) return null;

  // keep the desired order
  const order = [
    "Attiki",
    "Nisia Aigaiou - Kriti",
    "Voreia Ellada",
    "Kentriki Ellada",
  ];
  const map = Object.fromEntries(
    rows.map((r) => [r["Demographic Group"], num(r["2024"])])
  );
  const x = order.filter((k) => k in map);
  const y = x.map((k) => map[k]);

  return {
    title: { text: "AROPE by NUTS-1 Region 2024", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    grid: { left: 56, right: 24, top: 56, bottom: 40 },
    xAxis: { type: "category", data: x },
    yAxis: { type: "value", name: "%" },
    series: [{ type: "bar", data: y }],
  };
}

function riskOfPovertyByNUTS2(elements) {
  const rows = elements.filter((r) => r["Demo Group Type"] === "NUTS-2");
  if (!rows.length) return null;

  // gather union of year columns across these rows (typically 2020..2023)
  const years = Array.from(new Set(rows.flatMap((r) => yearCols(r))))
    .filter((y) => y >= "2020" && y <= "2023")
    .sort();

  const series = rows.map((r) => ({
    name: r["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(r[y])),
  }));

  return {
    title: { text: "Risk of Poverty by NUTS-2 Region", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis" },
    legend: { type: "scroll", top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

/* ---------- Age pyramid (Greece) ---------- */

function agePyramidGreece(elements) {
  const rows = elements.filter(
    (r) => r["Demographic Group"] === "Total Greece"
  );
  if (!rows.length) return null;

  const ages = rows.map((r) => String(r["Age Group"]));
  const yCats = [...ages].reverse();
  // In this dataset Men/Women are positive; make men negative for the left side
  const men = rows.map((r) => -num(r["Men"])).reverse();
  const women = rows.map((r) => num(r["Women"])).reverse();

  return {
    title: { text: "Age Pyramid Greece 2024", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 28 },
    grid: { left: 88, right: 48, top: 72, bottom: 24 },
    xAxis: [
      {
        type: "value",
        axisLabel: { formatter: (v) => Math.abs(v).toLocaleString() },
      },
    ],
    yAxis: [{ type: "category", data: yCats }],
    series: [
      { name: "Men", type: "bar", stack: "pop", data: men },
      { name: "Women", type: "bar", stack: "pop", data: women },
    ],
  };
}

/* ---------- Immigration (grouped bars) ---------- */

function immigration2020(elements) {
  const rows = elements.filter(
    (r) =>
      r["Demo Group Type"] === "Country of Birth" &&
      ["EU", "Non-EU Europe", "Non-Europe"].includes(
        String(r["Demographic Group"])
      )
  );
  if (!rows.length) return null;

  const x = rows.map((r) => String(r["Demographic Group"]));
  const women = rows.map((r) => num(r["Women"]));
  const men = rows.map((r) => num(r["Men"]));
  const both = rows.map((r) => num(r["Both"]));

  return {
    title: { text: "Total no. of Immigrants by Region 2020", left: "center", width: "90%", textStyle: { overflow: "break", lineHeight: 20 } },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 28 },
    grid: { left: 64, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: x },
    yAxis: { type: "value" },
    series: [
      { name: "Women", type: "bar", data: women },
      { name: "Men", type: "bar", data: men },
      { name: "Both", type: "bar", data: both },
    ],
  };
}

/* ---------- Social Protection Expenditure (multi-line) ---------- */

function socialProtectionExpenditure(elements) {
  if (!elements?.length) return null;
  const rows = elements.filter(
    (r) => r["Function"] && r["Function"] !== "Total"
  );
  const yrs = yearCols(rows[0]); // 2017..2022

  const series = rows.map((r) => ({
    name: r["Function"],
    type: "line",
    smooth: true,
    data: yrs.map((y) => num(r[y] === "-" ? null : r[y])),
  }));

  return {
    title: {
      text: "Social Protection Expenditure by Function (Millions of €)",
      left: "center",
    },
    tooltip: { trigger: "axis" },
    legend: { type: "scroll", top: 28 },
    grid: { left: 64, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: yrs },
    yAxis: { type: "value" },
    series,
  };
}

/* ---------- main ---------- */
export default function GreeceDashboard({ dataById, policyData }) {
  const charts = useMemo(() => {
    const out = [];

    // AROPE (4 charts)
    const arope = getEls(dataById, AROPE_ID);
    if (arope.length) {
      const c1 = applyTextColor(aropeByGender(arope));
      const c2 = applyTextColor(aropeByAge(arope));
      const c3 = applyTextColor(aropeByNUTS1_2024(arope));
      const c4 = applyTextColor(riskOfPovertyByNUTS2(arope));
      if (c1) out.push({ key: "gr-arope-gender", option: c1 });
      if (c2) out.push({ key: "gr-arope-age", option: c2 });
      if (c3) out.push({ key: "gr-arope-nuts1-2024", option: c3 });
      if (c4) out.push({ key: "gr-rop-nuts2", option: c4 });
    }

    // Age pyramid
    const age = getEls(dataById, AGE_ID);
    if (age.length) {
      const c = applyTextColor(agePyramidGreece(age));
      if (c) out.push({ key: "gr-age-pyr", option: c });
    }

    // Immigration
    const imm = getEls(dataById, IMM_ID);
    if (imm.length) {
      const c = applyTextColor(immigration2020(imm));
      if (c) out.push({ key: "gr-imm", option: c });
    }

    // Social Protection Expenditure
    const spe = getEls(dataById, SPE_ID);
    if (spe.length) {
      const c = applyTextColor(socialProtectionExpenditure(spe));
      if (c) out.push({ key: "gr-spe", option: c });
    }

    return out;
  }, [dataById]);

  if (!charts.length && !policyData) return null;

  return (
    <div className="chart-grid">
      {policyData && <ScreeningPolicyCard policyData={policyData} />}
      {charts.map((c) => (
        <ChartCard key={c.key} option={c.option} />
      ))}
    </div>
  );
}
